import { useState } from "react";
import { motion } from "framer-motion";
import { ArrowLeft, FileText, HeartHandshake, Save, Send, Loader2, ShieldCheck, Copy, FileDown, Lock, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { saveNote, sendNote, errMsg } from "@/api";
import { exportPatientPdf, exportClinicalPdf } from "@/lib/pdf";
import { useAuth } from "@/auth/AuthContext";

export const DocumentsView = ({ patient, appointmentId = null, initial, fragmentIds = [], audioDeleted, onBack, onDone }) => {
  const { user } = useAuth();
  const canExportClinical = ["therapist", "clinic_admin", "super_admin"].includes(user?.role);
  const [clinical, setClinical] = useState(initial.clinical_record || "");
  const [patientDoc, setPatientDoc] = useState(initial.patient_document || "");
  const [noteId, setNoteId] = useState(initial.id || null);
  const [saved, setSaved] = useState(!!initial.id);
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);

  const persist = async () => {
    const note = await saveNote({
      patient_id: patient.id,
      appointment_id: appointmentId,
      transcript: initial.transcript || "",
      clinical_record: clinical,
      patient_document: patientDoc,
      // Las notas sueltas de la sesión se borran en el servidor en cuanto existe la nota que las resume.
      fragment_ids: fragmentIds,
    });
    setNoteId(note.id);
    setSaved(true);
    return note;
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await persist();
      toast.success("Nota guardada");
    } catch (err) {
      toast.error(errMsg(err, "Error al guardar la nota"));
    } finally {
      setSaving(false);
    }
  };

  const handleSend = async () => {
    setSending(true);
    try {
      let id = noteId;
      if (!saved || !id) {
        const note = await persist();
        id = note.id;
      }
      const res = await sendNote(id);
      toast.success(res.message || "Documento enviado al paciente (simulado)");
      onDone();
    } catch (err) {
      toast.error(errMsg(err, "Error al enviar"));
    } finally {
      setSending(false);
    }
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(patientDoc);
      toast.success("Documento del paciente copiado");
    } catch {
      toast.error("No se pudo copiar");
    }
  };

  const handlePdf = () => {
    exportPatientPdf(patient.name, patientDoc);
    toast.success("PDF del paciente generado");
  };

  const handleClinicalPdf = () => {
    exportClinicalPdf(patient.name, clinical);
    toast.success("PDF clínico (confidencial) generado");
  };

  return (
    <div className="max-w-5xl mx-auto px-4 pt-4 pb-28">
      <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
        <Button variant="ghost" data-testid="documents-back-button" onClick={onBack} className="rounded-full text-slate-500">
          <ArrowLeft className="w-4 h-4" /> Volver
        </Button>
        {audioDeleted && (
          <Badge data-testid="audio-deleted-badge" className="bg-brand-teal/10 text-brand-teal hover:bg-brand-teal/10 border-0 rounded-full py-1.5 px-3">
            <ShieldCheck className="w-4 h-4 mr-1" /> Audio eliminado por seguridad
          </Badge>
        )}
      </div>

      <div>
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-brand-teal">Documentación</p>
        <h2 className="text-2xl font-semibold text-brand-navy">{patient.name}</h2>
        <div className="flex flex-wrap items-center gap-2 mt-1.5">
          <Badge data-testid="ai-generated-badge" className="bg-amber-100 text-amber-800 hover:bg-amber-100 border-0 rounded-full py-1 px-2.5">
            <Sparkles className="w-3.5 h-3.5 mr-1" /> Generado con IA · revisar
          </Badge>
          <p className="text-slate-500 text-sm">Borrador generado por IA: revísalo y edítalo antes de guardar o enviar.</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8 mt-6">
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="bg-white border border-slate-200 rounded-2xl shadow-sm p-6">
          <div className="flex items-center justify-between gap-2 mb-3">
            <div className="flex items-center gap-2 text-brand-navy font-medium">
              <FileText className="w-5 h-5" /> Registro clínico interno
            </div>
            {canExportClinical && (
              <Button
                variant="ghost"
                size="sm"
                data-testid="export-clinical-pdf-button"
                onClick={handleClinicalPdf}
                className="text-slate-500 hover:text-brand-navy hover:bg-slate-100 rounded-full h-8"
                title="Exportar PDF confidencial (uso interno)"
              >
                <Lock className="w-4 h-4" /> PDF interno
              </Button>
            )}
          </div>
          <Textarea
            data-testid="clinical-record-textarea"
            value={clinical}
            onChange={(e) => { setClinical(e.target.value); setSaved(false); }}
            className="min-h-[340px] font-mono text-sm leading-relaxed resize-y bg-slate-50/60 border-slate-200"
          />
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.08 }} className="bg-teal-50 border border-brand-teal/20 rounded-2xl shadow-sm p-6">
          <div className="flex items-center justify-between gap-2 mb-3">
            <div className="flex items-center gap-2 text-brand-navy font-medium">
              <HeartHandshake className="w-5 h-5 text-brand-teal" /> Documento para el paciente
            </div>
            <div className="flex items-center gap-1">
              <Button
                variant="ghost"
                size="sm"
                data-testid="copy-patient-doc-button"
                onClick={handleCopy}
                className="text-brand-teal hover:text-brand-teal hover:bg-brand-teal/10 rounded-full h-8"
              >
                <Copy className="w-4 h-4" /> Copiar
              </Button>
              <Button
                variant="ghost"
                size="sm"
                data-testid="export-pdf-button"
                onClick={handlePdf}
                className="text-brand-teal hover:text-brand-teal hover:bg-brand-teal/10 rounded-full h-8"
              >
                <FileDown className="w-4 h-4" /> PDF
              </Button>
            </div>
          </div>
          <Textarea
            data-testid="patient-document-textarea"
            value={patientDoc}
            onChange={(e) => { setPatientDoc(e.target.value); setSaved(false); }}
            className="min-h-[340px] text-sm leading-relaxed resize-y bg-white border-brand-teal/20"
          />
        </motion.div>
      </div>

      <div className="flex flex-col sm:flex-row gap-3 mt-6">
        <Button
          data-testid="save-note-button"
          onClick={handleSave}
          disabled={saving}
          variant="outline"
          className="rounded-full border-brand-navy text-brand-navy hover:bg-brand-navy hover:text-white h-12 flex-1"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          {saved ? "Guardado" : "Guardar"}
        </Button>
        <Button
          data-testid="send-patient-button"
          onClick={handleSend}
          disabled={sending}
          className="rounded-full bg-brand-navy hover:bg-brand-navy/90 text-white h-12 flex-1"
        >
          {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
          Enviar al paciente
        </Button>
      </div>
    </div>
  );
};
